import { money } from './drawerFormat.js';
import { VARIANCE_ACTIONS, varianceActionTargetFile } from './varianceActions.js';

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());
const codeOf = (row = {}) => text(row.productCode || row.oemProductCode || row.code).toUpperCase();
const priceOf = (row = {}) => Number(row.dealerCost ?? row.budget ?? row.price ?? 0) || 0;
const toMoneyNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = typeof value === 'string'
    ? Number(value.replace(/[$,\s]/g, ''))
    : Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : fallback;
};
const rowKeyOf = (row = {}) => text(
  row.sourceRowKey || row.targetRowKey || row.rowKey || row.storageKey || row.fileRowId || row.id || row.rowNumber,
).toLowerCase();

const countByCode = (rows = []) => rows.reduce((acc, row) => {
  const code = codeOf(row);
  if (!code) return acc;
  acc.set(code, (acc.get(code) || 0) + 1);
  return acc;
}, new Map());

const productCodeList = (rows = []) => {
  if (!rows.length) return '';
  const labels = rows
    .slice(0, 4)
    .map((row) => (row.count > 1 ? `${row.code} x${row.count}` : row.code));
  if (rows.length > 4) labels.push(`${rows.length - 4} more`);
  return labels.join(', ');
};

const humanExtraLabel = (source, rows = []) => {
  const count = rows.reduce((sum, row) => sum + row.count, 0);
  if (!count) return '';
  const codes = productCodeList(rows);
  return count === 1
    ? `${source} has an extra ${codes} code`
    : `${source} has ${count} extra ${source}-only codes: ${codes}`;
};

const priceByCode = (rows = []) => rows.reduce((acc, row) => {
  const code = codeOf(row);
  if (!code) return acc;
  const current = acc.get(code) || [];
  current.push(priceOf(row));
  acc.set(code, current);
  return acc;
}, new Map());

const firstMoneyValue = (source = {}, keys = []) => {
  for (const key of keys) {
    if (source[key] === null || source[key] === undefined || source[key] === '') continue;
    const value = toMoneyNumber(source[key], null);
    if (value !== null) return value;
  }
  return null;
};

const actionTargetFile = (action = {}) => {
  const explicit = action.targetSource || action.targetFile || varianceActionTargetFile(action.actionType);
  if (explicit === 'gm' || explicit === 'cpq') return explicit;
  if (action.source === 'gm' || action.source === 'cpq') return action.source;
  return null;
};

const actionBasePrice = (action = {}) => toMoneyNumber(
  action.originalPrice
  ?? action.sourceEvidence?.price
  ?? action.sourceEvidence?.dealerCost
  ?? action.sourceEvidence?.budget
  ?? action.price,
  0,
);

const actionProposedPrice = (action = {}) => toMoneyNumber(
  action.customPrice ?? action.proposedPrice ?? action.sourcePrice ?? action.targetEvidence?.price,
  actionBasePrice(action),
);

const actionProductCode = (action = {}) => text(
  action.customProductCode || action.replacementProductCode || action.sourceProductCode || action.productCode,
).toUpperCase();

const matchesActionRow = (row, action) => {
  const actionKeys = [
    action.sourceRowKey,
    action.targetRowKey,
    action.sourceEvidence?.rowKey,
    action.sourceEvidence?.sourceRowKey,
    action.targetEvidence?.rowKey,
    action.targetEvidence?.sourceRowKey,
  ].map((value) => text(value).toLowerCase()).filter(Boolean);
  const rowKey = rowKeyOf(row);
  if (rowKey && actionKeys.length) return actionKeys.includes(rowKey);
  return codeOf(row) === text(action.productCode).toUpperCase();
};

const withActionAdjustments = (rows = [], actions = [], fileKind) => {
  const adjusted = rows.map((row) => ({ ...row }));
  actions.forEach((action) => {
    if (action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) return;
    const targetFile = actionTargetFile(action);
    if (targetFile !== fileKind) return;

    const isAdd = action.actionType === VARIANCE_ACTIONS.ADD_TO_GM
      || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ
      || (action.actionType === VARIANCE_ACTIONS.CUSTOM && action.compiledMutation === 'add');
    const isRemove = action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM
      || action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
      || (action.actionType === VARIANCE_ACTIONS.CUSTOM && action.compiledMutation === 'remove');
    const isAdjust = action.actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT
      || action.actionType === VARIANCE_ACTIONS.CHANGE_PRICE
      || (action.actionType === VARIANCE_ACTIONS.CUSTOM && action.compiledMutation === 'adjust');

    if (isAdd) {
      const code = actionProductCode(action);
      if (!code) return;
      adjusted.push(fileKind === 'gm'
        ? { productCode: code, dealerCost: actionProposedPrice(action), isBilling: true, sourceRowKey: action.id }
        : { oemProductCode: code, budget: actionProposedPrice(action), quantity: 1, sourceRowKey: action.id });
      return;
    }

    const index = adjusted.findIndex((row) => matchesActionRow(row, action));
    if (index < 0) return;
    if (isRemove) {
      adjusted.splice(index, 1);
      return;
    }
    if (isAdjust) {
      const nextCode = actionProductCode(action);
      const nextPrice = actionProposedPrice(action);
      adjusted[index] = fileKind === 'gm'
        ? {
            ...adjusted[index],
            productCode: nextCode || adjusted[index].productCode,
            dealerCost: nextPrice,
            isBilling: true,
          }
        : {
            ...adjusted[index],
            oemProductCode: nextCode || adjusted[index].oemProductCode,
            budget: nextPrice,
            quantity: Number(adjusted[index].quantity ?? 1) > 0 ? adjusted[index].quantity : 1,
          };
    }
  });
  return adjusted;
};

export const buildBacDiffNarrative = ({
  gmLineItems = [],
  cpqLineItems = [],
  varianceActions = [],
  sourceTotals = {},
} = {}) => {
  const adjustedGmLineItems = withActionAdjustments(gmLineItems, varianceActions, 'gm');
  const adjustedCpqLineItems = withActionAdjustments(cpqLineItems, varianceActions, 'cpq');
  const gmBilling = adjustedGmLineItems.filter((row) => row.isBilling);
  const cpqBilling = adjustedCpqLineItems.filter((row) => Number(row.quantity ?? 1) > 0 && Number(row.budget ?? 0) > 0);
  const gmCounts = countByCode(gmBilling);
  const cpqCounts = countByCode(cpqBilling);
  const gmPrices = priceByCode(gmBilling);
  const cpqPrices = priceByCode(cpqBilling);
  const codes = new Set([...gmCounts.keys(), ...cpqCounts.keys()]);
  const gmExtra = [];
  const cpqExtra = [];
  let priceMismatchCount = 0;

  codes.forEach((code) => {
    const gmCount = gmCounts.get(code) || 0;
    const cpqCount = cpqCounts.get(code) || 0;
    if (gmCount > cpqCount) gmExtra.push({ code, count: gmCount - cpqCount });
    if (cpqCount > gmCount) cpqExtra.push({ code, count: cpqCount - gmCount });
    const pairs = Math.min(gmCount, cpqCount);
    const gmValues = [...(gmPrices.get(code) || [])].sort((a, b) => a - b);
    const cpqValues = [...(cpqPrices.get(code) || [])].sort((a, b) => a - b);
    for (let index = 0; index < pairs; index += 1) {
      if (Math.round(gmValues[index]) !== Math.round(cpqValues[index])) priceMismatchCount += 1;
    }
  });

  const parts = [];
  const gmExtraCount = gmExtra.reduce((sum, row) => sum + row.count, 0);
  const cpqExtraCount = cpqExtra.reduce((sum, row) => sum + row.count, 0);
  if (gmExtraCount) parts.push(`GM has ${gmExtraCount} extra product${gmExtraCount === 1 ? '' : 's'}`);
  if (cpqExtraCount) parts.push(`CPQ has ${cpqExtraCount} extra product${cpqExtraCount === 1 ? '' : 's'}`);
  if (priceMismatchCount) parts.push(`${priceMismatchCount} price mismatch${priceMismatchCount === 1 ? '' : 'es'}`);

  const calculatedGmTotal = gmBilling.reduce((sum, row) => sum + priceOf(row), 0);
  const calculatedCpqTotal = cpqBilling.reduce((sum, row) => sum + priceOf(row), 0);
  const sourceGmTotal = firstMoneyValue(sourceTotals, [
    'gmTotal',
    'modifiedGmTotal',
    'gm',
    'modifiedGm',
    'gmValue',
    'gmAmount',
    'gmBillingValue',
  ]);
  const sourceCpqTotal = firstMoneyValue(sourceTotals, [
    'cpqTotal',
    'modifiedCpqTotal',
    'cpq',
    'modifiedCpq',
    'cpqValue',
    'cpqAmount',
    'cpqBillingValue',
  ]);
  const calculatedDelta = calculatedGmTotal - calculatedCpqTotal;
  const sourceDelta = sourceGmTotal !== null && sourceCpqTotal !== null
    ? sourceGmTotal - sourceCpqTotal
    : null;
  const shouldUseSourceTotals = !parts.length
    && sourceDelta !== null
    && Math.abs(sourceDelta) > 1
    && Math.abs(sourceDelta - calculatedDelta) > 1;
  const gmTotal = shouldUseSourceTotals ? sourceGmTotal : calculatedGmTotal;
  const cpqTotal = shouldUseSourceTotals ? sourceCpqTotal : calculatedCpqTotal;
  const totalDelta = gmTotal - cpqTotal;
  const summary = parts.length
    ? parts.join(', ')
    : shouldUseSourceTotals
      ? 'GM and CPQ totals differ without product-level evidence'
      : 'GM and CPQ product sets line up by product code';
  const humanParts = [
    humanExtraLabel('GM', gmExtra),
    humanExtraLabel('CPQ', cpqExtra),
    priceMismatchCount ? `${priceMismatchCount} shared product${priceMismatchCount === 1 ? ' has a price mismatch' : 's have price mismatches'}` : '',
  ].filter(Boolean);
  const hasProductLevelEvidence = Boolean(gmBilling.length || cpqBilling.length);
  const humanSummary = humanParts.length
    ? humanParts.join('. ')
    : shouldUseSourceTotals
      ? hasProductLevelEvidence
        ? 'GM and CPQ totals differ, but product-level evidence does not identify the driver.'
        : 'GM and CPQ totals differ, but product-level evidence is not available for this BAC.'
      : 'GM and CPQ product codes line up for this BAC.';
  const deltaDirection = Math.abs(totalDelta) <= 1
    ? 'GM and CPQ are within $1.'
    : totalDelta > 0
      ? `GM is ${money(Math.abs(totalDelta))} higher than CPQ.`
      : `CPQ is ${money(Math.abs(totalDelta))} higher than GM.`;

  return {
    summary,
    humanSummary,
    gmExtra,
    cpqExtra,
    priceMismatchCount,
    gmTotal,
    cpqTotal,
    totalDelta,
    totalDeltaLabel: `${totalDelta >= 0 ? '+' : '-'}${money(Math.abs(totalDelta))}`,
    deltaDirection,
    hasUnexplainedTotalDelta: shouldUseSourceTotals,
  };
};
